import { AlertTriangle, ArrowRight, X, XCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { Banner } from '@/components/ui/banner';
import { BUTTON_SIZE, BUTTON_VARIANT } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '@/components/ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { useSubscriptionRecoveryReason } from '@/features/billing/hooks/use-subscription-recovery-reason';
import { useShouldDeferBillingToOnboarding } from '@/features/billing/hooks/use-should-defer-billing-to-onboarding';
import { useDismissibleBanner } from '@/hooks/browser/use-dismissible-banner';
import { useOnboardingLadder } from '@/features/setup/hooks/use-onboarding-ladder';
import { useIntegrationHealthStatus } from '@/features/workspace/hooks/use-integration-health-status';
import { selectTopBarNotice } from '@/features/setup/helpers/top-bar-notice';
import { TopBarNoticeKind } from '@/features/setup/types/top-bar-notice-enums';

const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // dismissals return after a day

const NOTICE_ICON: Partial<Record<TopBarNoticeKind, typeof AlertTriangle>> = {
  [TopBarNoticeKind.HEALTH_UNHEALTHY]: XCircle,
  [TopBarNoticeKind.HEALTH_UNKNOWN]: AlertTriangle,
};

export function GlobalTopBarNotice() {
  const location = useLocation();
  const ladder = useOnboardingLadder();
  const { status: healthStatus, isLoading: isHealthLoading } = useIntegrationHealthStatus();
  const recoveryReason = useSubscriptionRecoveryReason();
  const deferBillingToOnboarding = useShouldDeferBillingToOnboarding(recoveryReason);

  // An unresolved ladder reports every step incomplete; announcing "Finish
  // setup: Connect your Shopify store" to a live account is worse than waiting.
  // Don't check isError — cached data is still valid after a refetch failure.
  const onboardingStep =
    ladder.nextStep && !ladder.isLoading
      ? { id: ladder.nextStep.id, label: ladder.nextStep.label, href: ladder.nextStep.href }
      : null;
  const notice = selectTopBarNotice({ onboardingStep, healthStatus });

  const { isDismissed, dismiss } = useDismissibleBanner({
    bannerId: 'global-top-bar',
    signal: notice?.signal,
    ttlMs: DISMISS_TTL_MS,
  });

  if (isHealthLoading || !notice) {
    return null;
  }

  // Home and Get Started own their guidance (the recommended-action card /
  // focused shell), so the global reminder stays out of their way there.
  if (location.pathname === '/home' || location.pathname.startsWith('/get-started')) {
    return null;
  }

  // Payment recovery takes over the shell above onboarding/health — except
  // during a trial with incomplete onboarding, where onboarding wins (getting
  // the merchant started matters more than a soft payment nudge).
  if (recoveryReason && !deferBillingToOnboarding) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  const Icon = NOTICE_ICON[notice.kind];
  const isOnboarding = notice.kind === TopBarNoticeKind.ONBOARDING;
  const compactActionLabel = isOnboarding ? 'Setup' : 'Sources';
  const role = isOnboarding
    ? 'region'
    : notice.kind === TopBarNoticeKind.HEALTH_UNHEALTHY
      ? 'alert'
      : 'status';

  return (
    <Banner.Root
      aria-label={isOnboarding ? 'Setup reminder' : undefined}
      role={role}
      tone={notice.tone}
    >
      <Banner.Content>
        {Icon ? (
          <Banner.Icon>
            <Icon />
          </Banner.Icon>
        ) : null}
        <Banner.Message className="text-content-primary">{notice.message}</Banner.Message>
      </Banner.Content>
      <Banner.Actions>
        <Button asChild size={BUTTON_SIZE.BANNER} variant={BUTTON_VARIANT.OUTLINE}>
          <Link aria-label={notice.actionLabel} to={notice.href}>
            <Banner.ActionLabel compact={compactActionLabel}>
              {notice.actionLabel}
            </Banner.ActionLabel>
            <ArrowRight aria-hidden="true" className="hidden size-3.5 @[420px]:block" />
          </Link>
        </Button>
      </Banner.Actions>
      <Banner.Dismiss>
        <IconButton
          label={isOnboarding ? 'Dismiss setup reminder' : 'Dismiss status notice'}
          onClick={dismiss}
          size={ICON_BUTTON_SIZE.BANNER}
          variant={ICON_BUTTON_VARIANT.GHOST}
        >
          <X />
        </IconButton>
      </Banner.Dismiss>
    </Banner.Root>
  );
}

export default GlobalTopBarNotice;
